import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  groupByDay,
  sampleAt,
  dirLabel,
  arrowRotation,
  swellColor,
  windColor,
  fmtFt,
  fmtKn,
  fmtPeriod,
  tideStateArrow,
  mToFt,
  kmhToKn,
  compass16,
} from '../utils/conditions';
import {
  evaluateAlerts,
  evaluateStrike,
  resolveWindArc,
  hourPeriodS,
  hourWindKn,
  swellTrains,
  face2Ft,
  surfFaceFt,
  combinedSurf,
  waveConsistency,
  consistencyColor,
  consistencyLabel,
  spotStrikeDefaults,
  tideOk,
  tideStageLabel,
  DEFAULT_TIDE,
  TIDE_MODE_LABEL,
  calibrateFt,
  windQuality,
  WIND_QUALITY_COLOR,
  WIND_QUALITY_LABEL,
  DEFAULT_THRESHOLDS,
  DEFAULT_CALIB,
} from '../utils/forecast';
import {
  useForecastSpots,
  seedDefaultSpots,
  ensureDefaultSpots,
  ensureSpotCalibrations,
  ensureSpotCams,
  DEFAULT_STRIKES,
  addSpot,
  removeSpot,
  setSpotThresholds,
  setSpotStrike,
  setSpotCalibration,
  setSpotTide,
  updateSpot,
  loadSpotForecast,
} from '../data/forecastSpots';
import { useAuth } from '../AuthContext';
import StrikeMiniMap from '../components/StrikeMiniMap';
import SpotCams from '../components/SpotCams';
import SwellWindow from '../components/SwellWindow';
import { useBasinArchive, loadSpotArchive } from '../data/swellArchive';

// Open-Meteo's wave models (NCEP GFS Wave, ECMWF WAM) top out at 16 days — the
// furthest any free swell source forecasts. Days past ~10 are lower confidence.
const FORECAST_DAYS = 16;
const CONFIDENCE_DAYS = 10; // beyond this we flag the outlook as lower-confidence

let uidSeq = 0;
const useUid = (p) => useMemo(() => `${p}-${(uidSeq += 1)}`, [p]);

// Measure a container's pixel width (for crisp, non-distorted SVG charts).
function useMeasure() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function windowLabel(cond, sec) {
  const off = (cond?.utcOffsetSec || 0) * 1000;
  const d = new Date(sec * 1000 + off);
  const day = d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  const time = d.toLocaleTimeString([], { hour: 'numeric', timeZone: 'UTC' });
  return `${day} ${time}`;
}

// "Sat 21" style day label in the spot's local time.
function dayLabel(cond, sec) {
  const off = (cond?.utcOffsetSec || 0) * 1000;
  const d = new Date(sec * 1000 + off);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Compact duration label from a count of hours (e.g. "2.5 days", "18 hrs").
function durationLabel(hours) {
  if (hours >= 24) {
    const days = hours / 24;
    return `${days >= 10 ? Math.round(days) : Math.round(days * 10) / 10} days`;
  }
  return `${hours} hr${hours === 1 ? '' : 's'}`;
}

// How many calendar days from now until a timestamp (rounded, min 0).
function daysOut(sec, nowMs = Date.now()) {
  return Math.max(0, Math.round((sec * 1000 - nowMs) / 86400000));
}

// Load every tracked spot's forecast once at the page level (cache-first, small
// concurrency pool) so both the Strike Missions section and the per-spot cards
// read from a single shared fetch instead of each card fetching on its own.
function useAllForecasts(spots, refreshKey) {
  const [map, setMap] = useState({});
  const key = useMemo(
    () => (spots || []).map((s) => `${s.id}:${s.lat}:${s.lng}`).join('|'),
    [spots]
  );
  useEffect(() => {
    if (!spots || !spots.length) {
      setMap({});
      return undefined;
    }
    let alive = true;
    setMap((prev) => {
      const next = {};
      spots.forEach((s) => {
        next[s.id] = prev[s.id]?.cond ? prev[s.id] : { loading: true };
      });
      return next;
    });
    const queue = [...spots];
    let active = 0;
    const pump = () => {
      if (!alive) return;
      while (active < 3 && queue.length) {
        const s = queue.shift();
        active += 1;
        loadSpotForecast(s, FORECAST_DAYS)
          .then((cond) => alive && setMap((m) => ({ ...m, [s.id]: { cond } })))
          .catch((e) => alive && setMap((m) => ({ ...m, [s.id]: { err: e.message } })))
          .finally(() => {
            active -= 1;
            pump();
          });
      }
    };
    pump();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshKey]);
  return map;
}

// Load every tracked spot's archived hourly history for a past window, shaped
// like the live `cond` so SpotCard/Meteogram render it unchanged.
function useAllSpotArchives(spots, win) {
  const [map, setMap] = useState({});
  const active = win?.mode === 'history' && Number.isFinite(win.startSec) && Number.isFinite(win.endSec);
  const key = useMemo(
    () => (active ? `${Math.round(win.startSec)}:${Math.round(win.endSec)}:${(spots || []).map((s) => s.id).join(',')}` : ''),
    [active, win, spots]
  );
  useEffect(() => {
    if (!active || !spots?.length) { setMap({}); return undefined; }
    let alive = true;
    setMap(Object.fromEntries(spots.map((s) => [s.id, { loading: true }])));
    const queue = [...spots];
    let running = 0;
    const pump = () => {
      if (!alive) return;
      while (running < 3 && queue.length) {
        const s = queue.shift();
        running += 1;
        loadSpotArchive(s.id, win.startSec, win.endSec)
          .then((cond) => alive && setMap((m) => ({ ...m, [s.id]: cond ? { cond } : { empty: true } })))
          .catch((e) => alive && setMap((m) => ({ ...m, [s.id]: { err: e.message } })))
          .finally(() => { running -= 1; pump(); });
      }
    };
    pump();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

// Collapsible section with a persisted open/closed state.
function Section({ id, title, sub, defaultOpen = true, right, children }) {
  const storeKey = `cs-fc-sec-${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storeKey);
      return v == null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () =>
    setOpen((o) => {
      try {
        localStorage.setItem(storeKey, !o ? '1' : '0');
      } catch {
        /* ignore */
      }
      return !o;
    });
  return (
    <section className={`cs-fc-section ${open ? 'is-open' : 'is-closed'}`}>
      <div className="cs-fc-sechead">
        <button className="cs-fc-sectoggle" onClick={toggle} aria-expanded={open}>
          <span className="cs-fc-secchev">{open ? '▾' : '▸'}</span>
          <h2>{title}</h2>
          {sub && <span className="cs-fc-secsub">{sub}</span>}
        </button>
        {right && <div className="cs-fc-secright">{right}</div>}
      </div>
      {open && <div className="cs-fc-secbody">{children}</div>}
    </section>
  );
}

// Night intervals [startSec,endSec] from daily sunrise/sunset, for shading.
function nightIntervals(cond) {
  const sr = cond?.daily?.sunrise || [];
  const ss = cond?.daily?.sunset || [];
  const out = [];
  const t0 = cond.rangeStart;
  const t1 = cond.rangeEnd;
  if (sr.length && t0 < sr[0]) out.push([t0, sr[0]]);
  for (let i = 0; i < ss.length; i += 1) {
    const start = ss[i];
    const end = sr[i + 1] != null ? sr[i + 1] : t1;
    if (end > start) out.push([start, Math.min(end, t1)]);
  }
  return out.map(([a, b]) => [Math.max(a, t0), Math.min(b, t1)]).filter(([a, b]) => b > a);
}

// ----------------------------- Full meteogram -----------------------------
const SWELL_TRAIN_LABEL = ['Primary', '2nd', '3rd'];
const SWELL_TRAIN_COLOR = ['#9aa6b5', '#b89cff', '#6fb3ff'];

// Compact raw-swell partition breakdown (P1/P2/P3 — height @ peak-period · dir),
// shared by the meteogram and strike tooltips so the raw energy is always visible.
function SwellTipRows({ trains }) {
  if (!trains || !trains.length) return null;
  return trains.map((t, i) => (
    <div className="cs-meteo-tip-row" key={i}>
      <span className="cs-meteo-tip-k">{SWELL_TRAIN_LABEL[i] || `#${i + 1}`}</span>
      <span className="cs-meteo-tip-v" style={{ color: SWELL_TRAIN_COLOR[i] || '#9aa6b5' }}>
        {fmtFt(t.ft)}{t.period != null ? ` @ ${fmtPeriod(t.period)}` : ''}{t.dir != null ? ` ${dirLabel(t.dir, false)}` : ''}
      </span>
    </div>
  ));
}

function Meteogram({ cond, alerts, beachFacing, tall, showLegend = true, calib = null, history = false }) {
  const [ref, w] = useMeasure();
  const gid = useUid('meteo');
  const hid = useUid('hatch');

  const geo = useMemo(() => {
    if (!w || !cond?.hours?.length) return null;
    const hours = cond.hours;
    const t0 = cond.rangeStart;
    const t1 = cond.rangeEnd;
    const span = t1 - t0 || 1;

    const padL = 30;
    const padR = 12;
    const padT = 16; // room for section tags
    const swellH = tall ? 188 : 150;
    const gap1 = 18; // wind ribbon gap (room for WIND tag)
    const windH = tall ? 56 : 50;
    const axisH = 20;
    const H = padT + swellH + gap1 + windH + axisH;

    const x = (t) => padL + ((w - padL - padR) * (t - t0)) / span;

    // Swell band — the forecast surf (calibrated combined face from all swell
    // trains when the spot is calibrated)
    const fts = hours.map((h) => surfFaceFt(h, calib));
    const maxFt = Math.max(...fts.filter((v) => v != null), 1);
    const niceMax = Math.max(2, Math.ceil(maxFt * 1.12));
    const ySB = padT + swellH;
    const yS = (ft) => ySB - ((ft || 0) / niceMax) * swellH;
    const sPts = hours.map((h, i) => (fts[i] == null ? null : [x(h.t), yS(fts[i])])).filter(Boolean);
    const sLine = sPts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('');
    const sArea = sPts.length
      ? `${sLine}L${sPts[sPts.length - 1][0].toFixed(1)},${ySB}L${sPts[0][0].toFixed(1)},${ySB}Z`
      : '';

    // Secondary swell train: a thin line only (no fill) so both trains read.
    const s2fts = hours.map((h) => face2Ft(h, calib));
    const s2segs = []; // split into segments so gaps (null) don't bridge
    let cur = [];
    hours.forEach((h, i) => {
      if (s2fts[i] == null) {
        if (cur.length > 1) s2segs.push(cur);
        cur = [];
      } else {
        cur.push([x(h.t), yS(s2fts[i])]);
      }
    });
    if (cur.length > 1) s2segs.push(cur);
    const hasSecondary = s2segs.length > 0;
    const s2Lines = s2segs.map((seg) => seg.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(''));

    // Period labels: one per day at that day's peak surf (clean, sparse — replaces
    // the old every-3h dot clutter). Drop any label that would collide with the
    // previous one so closely-spaced days don't pile up unreadably.
    const periodLabels = [];
    let lastLabelX = -1e9;
    const minLabelGap = 26;
    groupByDay(cond).forEach((d) => {
      let best = null;
      d.hours.forEach((h) => {
        const ft = surfFaceFt(h, calib);
        const p = hourPeriodS(h);
        if (ft == null || p == null) return;
        if (!best || ft > best.ft) best = { ft, p, t: h.t };
      });
      if (!best) return;
      const lx = x(best.t);
      if (lx - lastLabelX < minLabelGap) return;
      lastLabelX = lx;
      periodLabels.push({ x: lx, y: yS(best.ft) - 7, p: Math.round(best.p) });
    });

    // Wind ribbon
    const yWT = ySB + gap1;
    const yWM = yWT + windH / 2;
    const yWB = yWT + windH; // plot bottom (tide removed)
    const avail = w - padL - padR;
    const wantArrows = Math.max(8, Math.floor(avail / 24));
    const stepH = Math.max(2, Math.round(hours.length / wantArrows));
    const arrows = [];
    for (let i = 0; i < hours.length; i += stepH) {
      const h = hours[i];
      const kn = hourWindKn(h);
      if (kn == null || h.windDir == null) continue;
      const q = windQuality(h.windDir, beachFacing, kn);
      const color = (q && WIND_QUALITY_COLOR[q]) || '#9aa0a6';
      const len = 9 + Math.min(kn, 30) / 30 * 13;
      arrows.push({ x: x(h.t), travel: arrowRotation(h.windDir), color, len, kn, q, from: h.windDir });
    }

    // Night shading + good windows + days + now
    const nights = nightIntervals(cond).map(([a, b]) => ({ x: x(a), w: x(b) - x(a) }));
    const goods = (alerts?.windows || []).map((win) => ({ x: x(win.startSec), w: Math.max(1.5, x(win.endSec) - x(win.startSec)) }));
    const off = (cond.utcOffsetSec || 0) * 1000;
    const dayGroups = groupByDay(cond);
    const days = dayGroups.map((d, idx) => {
      const a = x(d.hours[0].t);
      const ld = new Date(d.hours[0].t * 1000 + off);
      const dom = ld.getUTCDate();
      const mon = ld.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
      // Show the month only at the start and when it actually rolls over.
      return { key: d.key, dom, mon, showMon: idx === 0 || dom === 1, x0: a, boundary: idx > 0 };
    });

    // Per-day height tint: horizontal gradient stops colored by each day's
    // peak surf (same ramp as the strike calendar), so the chart glance-reads
    // size day by day. Stops are feathered slightly at day boundaries.
    const gradW = w - padL - padR || 1;
    const feather = 0.008;
    const dayStops = dayGroups.map((d, idx) => {
      let peak = null;
      d.hours.forEach((h) => {
        const ft = surfFaceFt(h, calib);
        if (ft != null && (peak == null || ft > peak)) peak = ft;
      });
      const xa = x(d.hours[0].t);
      const xb = idx < dayGroups.length - 1 ? x(dayGroups[idx + 1].hours[0].t) : w - padR;
      return {
        a: Math.max(0, (xa - padL) / gradW + (idx > 0 ? feather : 0)),
        b: Math.min(1, (xb - padL) / gradW - (idx < dayGroups.length - 1 ? feather : 0)),
        color: strikeColor(peak),
      };
    });
    const nowSec = Date.now() / 1000;
    const nowX = nowSec >= t0 && nowSec <= t1 ? x(nowSec) : null;

    const gridFts = [0, niceMax / 2, niceMax];
    const plotW = w - padL - padR;
    const hourX = hours.map((h) => x(h.t));
    const confSec = t0 + CONFIDENCE_DAYS * 86400;
    const extendedX = history ? null : (confSec < t1 ? x(confSec) : null);

    return {
      H, padL, padR, padT, ySB, yWT, yWM, yWB, windH, niceMax, yS,
      sArea, sLine, s2Lines, hasSecondary, periodLabels, arrows,
      nights, goods, days, dayStops, nowX, gridFts, plotW, hourX, len: hours.length, extendedX,
    };
  }, [w, cond, alerts, beachFacing, tall, calib, history]);

  const [hoverIdx, setHoverIdx] = useState(null);
  const hours = cond?.hours || [];
  const onMove = (e) => {
    if (!geo || !geo.len) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) * w) / (rect.width || w);
    const frac = (mx - geo.padL) / (geo.plotW || 1);
    const idx = Math.max(0, Math.min(geo.len - 1, Math.round(frac * (geo.len - 1))));
    setHoverIdx(idx);
  };
  const onLeave = () => setHoverIdx(null);

  const hv = (() => {
    if (geo == null || hoverIdx == null || !hours[hoverIdx]) return null;
    const h = hours[hoverIdx];
    const tr = swellTrains(h);
    const cs = combinedSurf(h, calib);
    const ft = cs.ft;
    const kn = hourWindKn(h);
    const q = h.windDir != null ? windQuality(h.windDir, beachFacing, kn) : null;
    return {
      x: geo.hourX[hoverIdx],
      y: ft != null ? geo.yS(ft) : geo.padT,
      time: windowLabel(cond, h.t),
      ft, raw: cs.hs, per: cs.period, swellDir: cs.dir,
      trains: [tr.primary, tr.secondary, tr.tertiary].filter((t) => t && t.ft != null),
      cons: waveConsistency(h),
      kn, windDir: h.windDir, q,
    };
  })();

  return (
    <div className="cs-meteo" ref={ref}>
      {geo && (
        <svg
          width={w}
          height={geo.H}
          viewBox={`0 0 ${w} ${geo.H}`}
          className="cs-meteo-svg"
          onPointerMove={onMove}
          onPointerDown={onMove}
          onPointerLeave={onLeave}
        >
          <defs>
            {/* horizontal per-day tint: each day's slice colored by its peak surf */}
            <linearGradient id={gid} x1={geo.padL} y1="0" x2={w - geo.padR} y2="0" gradientUnits="userSpaceOnUse">
              {geo.dayStops.map((s, i) => (
                <React.Fragment key={i}>
                  <stop offset={`${(s.a * 100).toFixed(2)}%`} stopColor={s.color} />
                  <stop offset={`${(s.b * 100).toFixed(2)}%`} stopColor={s.color} />
                </React.Fragment>
              ))}
            </linearGradient>
            {/* vertical fade so the fill still melts toward the baseline */}
            <linearGradient id={`${gid}-v`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.75" />
              <stop offset="55%" stopColor="#fff" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0.08" />
            </linearGradient>
            <mask id={`${gid}-fade`}>
              <rect x={geo.padL} y={geo.padT} width={geo.plotW} height={geo.ySB - geo.padT} fill={`url(#${gid}-v)`} />
            </mask>
            <pattern id={hid} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="rgba(230,195,74,0.05)" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(230,195,74,0.16)" strokeWidth="1" />
            </pattern>
          </defs>

          {/* night shading (cool navy) across the whole plot */}
          {geo.nights.map((n, i) => (
            <rect key={`n${i}`} x={n.x} y={geo.padT} width={n.w} height={geo.yWB - geo.padT} fill="rgba(10,16,40,0.55)" />
          ))}
          {/* lower-confidence "extended outlook" region (days 10-16) */}
          {geo.extendedX != null && (
            <g>
              <rect x={geo.extendedX} y={geo.padT} width={w - geo.padR - geo.extendedX} height={geo.yWB - geo.padT} fill={`url(#${hid})`} />
              <line x1={geo.extendedX} y1={geo.padT} x2={geo.extendedX} y2={geo.yWB} stroke="rgba(230,195,74,0.5)" strokeDasharray="4 3" />
              <text x={geo.extendedX + 5} y={geo.padT + 9} textAnchor="start" className="cs-meteo-ext">extended · lower confidence</text>
            </g>
          )}
          {/* date period markers: a divider at each local midnight + a tick */}
          {geo.days.map((d) => (
            d.boundary ? (
              <g key={`sep${d.key}`}>
                <line x1={d.x0} y1={geo.padT} x2={d.x0} y2={geo.yWB} stroke="rgba(255,255,255,0.14)" strokeDasharray="2 4" />
                <line x1={d.x0} y1={geo.yWB} x2={d.x0} y2={geo.yWB + 4} stroke="rgba(255,255,255,0.4)" />
              </g>
            ) : null
          ))}
          {/* good-surf windows: teal tint + bottom accent bar (distinct from night) */}
          {geo.goods.map((g, i) => (
            <g key={`g${i}`}>
              <rect x={g.x} y={geo.padT} width={g.w} height={geo.ySB - geo.padT} fill="rgba(20,224,200,0.10)" />
              <rect x={g.x} y={geo.ySB - 2.5} width={g.w} height={2.5} fill="rgba(20,224,200,0.85)" />
            </g>
          ))}
          {/* day labels anchored to the start of each date's section */}
          {geo.days.map((d) => (
            <text
              key={`dl${d.key}`}
              x={Math.min(d.x0 + 3, w - geo.padR - (d.showMon ? 30 : 14))}
              y={geo.H - 5}
              textAnchor="start"
              className="cs-meteo-daylbl"
            >
              {d.showMon ? <tspan className="cs-meteo-mon">{d.mon} </tspan> : null}{d.dom}
            </text>
          ))}
          {/* swell ft gridlines */}
          {geo.gridFts.map((ft, i) => (
            <g key={`grid${i}`}>
              <line x1={geo.padL} y1={geo.yS(ft)} x2={w - geo.padR} y2={geo.yS(ft)} stroke="rgba(255,255,255,0.07)" strokeDasharray={i === 0 ? '0' : '2 3'} />
              <text x={geo.padL - 4} y={geo.yS(ft) + 3} textAnchor="end" className="cs-meteo-axis">{ft % 1 === 0 ? ft : ft.toFixed(1)}</text>
            </g>
          ))}
          {/* section tags */}
          <text x={geo.padL - 4} y={geo.padT - 4} textAnchor="start" className="cs-meteo-tag">{calib?.enabled ? 'SURF ft' : 'SWELL ft'}</text>
          <text x={w - geo.padR} y={geo.yWT - 5} textAnchor="end" className="cs-meteo-tag">WIND</text>
          {/* swell area + primary line (day-tinted fill, faded to the baseline) */}
          {geo.sArea && <path d={geo.sArea} fill={`url(#${gid})`} mask={`url(#${gid}-fade)`} />}
          {geo.s2Lines.map((d, i) => (
            <path key={`s2${i}`} d={d} fill="none" stroke="#b89cff" strokeWidth="1.3" strokeOpacity="0.85" strokeDasharray="4 2.5" />
          ))}
          {geo.sLine && <path d={geo.sLine} fill="none" stroke="#8af0e0" strokeWidth="1.8" />}
          {/* daily peak period labels (sparse, replaces dot clutter) */}
          {geo.periodLabels.map((d, i) => (
            <g key={`pl${i}`}>
              <circle cx={d.x} cy={d.y + 7} r="2" fill="#ffcf7a" />
              <text x={d.x} y={d.y} textAnchor="middle" className="cs-meteo-per">{d.p}s</text>
            </g>
          ))}
          {/* wind ribbon baseline */}
          <line x1={geo.padL} y1={geo.yWM} x2={w - geo.padR} y2={geo.yWM} stroke="rgba(255,255,255,0.06)" />
          {/* wind ribbon arrows */}
          {geo.arrows.map((a, i) => (
            <g key={`w${i}`} transform={`translate(${a.x},${geo.yWM}) rotate(${a.travel})`}>
              <line x1="0" y1={a.len / 2} x2="0" y2={-a.len / 2} stroke={a.color} strokeWidth="1.7" strokeLinecap="round" />
              <path d={`M0,${(-a.len / 2 - 3.8).toFixed(1)} L-2.8,${(-a.len / 2 + 1).toFixed(1)} L2.8,${(-a.len / 2 + 1).toFixed(1)} Z`} fill={a.color} />
              <title>{`${Math.round(a.kn)} kn ${dirLabel(a.from, false)}${a.q ? ` · ${WIND_QUALITY_LABEL[a.q]}` : ''}`}</title>
            </g>
          ))}
          {/* now line */}
          {geo.nowX != null && (
            <g>
              <line x1={geo.nowX} y1={geo.padT} x2={geo.nowX} y2={geo.yWB} stroke="rgba(255,255,255,0.6)" strokeWidth="1" strokeDasharray="3 3" />
              <rect x={geo.nowX - 13} y={0} width={26} height={12} rx={3} fill="rgba(255,255,255,0.14)" />
              <text x={geo.nowX} y={2} textAnchor="middle" className="cs-meteo-now">NOW</text>
            </g>
          )}
          {/* hover crosshair */}
          {hv && (
            <g pointerEvents="none">
              <line x1={hv.x} y1={geo.padT} x2={hv.x} y2={geo.yWB} stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
              {hv.ft != null && <circle cx={hv.x} cy={hv.y} r="3.5" fill="#fff" stroke="#0c1018" strokeWidth="1.2" />}
            </g>
          )}
          {/* transparent capture layer (kept last so it receives all pointer events) */}
          <rect x={geo.padL} y={geo.padT} width={geo.plotW} height={geo.yWB - geo.padT} fill="transparent" style={{ cursor: 'crosshair' }} />
        </svg>
      )}
      {hv && (
        <div
          className="cs-meteo-tip"
          style={{ left: `${Math.max(8, Math.min(w - 8, hv.x))}px` }}
        >
          <div className="cs-meteo-tip-time">{hv.time}</div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">{calib?.enabled ? 'Surf' : 'Swell'}</span>
            <span className="cs-meteo-tip-v" style={{ color: swellColor(hv.ft) }}>
              {fmtFt(hv.ft)}{hv.per != null ? ` @ ${fmtPeriod(hv.per)}` : ''}{hv.swellDir != null ? ` ${dirLabel(hv.swellDir, false)}` : ''}
            </span>
          </div>
          <SwellTipRows trains={hv.trains} />
          {hv.cons != null && (
            <div className="cs-meteo-tip-row">
              <span className="cs-meteo-tip-k">Sets</span>
              <span className="cs-meteo-tip-v">
                <i style={{ background: consistencyColor(hv.cons) }} />
                {hv.cons} · {consistencyLabel(hv.cons)}
              </span>
            </div>
          )}
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Wind</span>
            <span className="cs-meteo-tip-v">
              <i style={{ background: (hv.q && WIND_QUALITY_COLOR[hv.q]) || '#9aa0a6' }} />
              {fmtKn(hv.kn)}{hv.windDir != null ? ` ${dirLabel(hv.windDir, false)}` : ''}{hv.q ? ` · ${WIND_QUALITY_LABEL[hv.q]}` : ''}
            </span>
          </div>
        </div>
      )}
      {showLegend && (
      <div className="cs-meteo-legend">
        <span><i style={{ background: WIND_QUALITY_COLOR.offshore }} /> Offshore</span>
        <span><i style={{ background: WIND_QUALITY_COLOR.cross }} /> Cross</span>
        <span><i style={{ background: WIND_QUALITY_COLOR.onshore }} /> Onshore</span>
        <span><i style={{ background: WIND_QUALITY_COLOR.glass }} /> Glassy</span>
        <span className="cs-meteo-legend-sep">•</span>
        <span><i style={{ background: '#8af0e0' }} /> forecast surf</span>
        {geo?.hasSecondary && <span><i style={{ background: '#b89cff' }} /> 2nd swell</span>}
        <span><i className="dot" style={{ background: '#ffcf7a' }} /> daily peak period</span>
        <span><i className="night" /> night</span>
        <span><i style={{ background: 'rgba(20,224,200,0.85)' }} /> alert window</span>
      </div>
      )}
    </div>
  );
}

// Compact one-line "now" readout (swell · wind · tide) — keeps the card tight
// so the meteogram chart gets the space.
function NowStrip({ cond, calib, tide }) {
  const now = sampleAt(cond, Date.now());
  const sample = now || (cond?.hours?.length ? cond.hours.find((h) => h.t * 1000 >= Date.now()) || cond.hours[0] : null);
  if (!sample) return <div className="cs-fc-nowstrip cs-fc-nowstrip-empty">No current reading</div>;
  const tr = swellTrains(sample);
  const trains = [tr.primary, tr.secondary, tr.tertiary].filter((t) => t && t.ft != null);
  const swellDir = tr.primary.dir;
  const windKn = kmhToKn(sample.windSpd);
  const tideFt = mToFt(sample.tideM);
  const calibrated = !!calib?.enabled;
  const cs = combinedSurf(sample, calib);
  const faceVal = cs.ft;
  const cons = waveConsistency(sample);
  const stage01 = sample.tideStage01;
  const tidePref = tide?.mode && tide.mode !== 'any' ? tide.mode : null;
  const tideMatch = tidePref ? tideOk(sample, tide) : null;
  return (
    <div className="cs-fc-nowstrip">
      <span className="cs-fc-now-now">NOW</span>
      <span className="cs-fc-chip" title={calibrated ? 'Calibrated breaking/face height for this spot, at the blended (combined-swell) peak period' : 'Combined-swell height at the blended peak period'}>
        <em>{calibrated ? 'Face' : 'Swell'}</em>
        <b style={{ color: swellColor(faceVal) }}>{fmtFt(faceVal)}{cs.period != null ? ` @ ${fmtPeriod(cs.period)}` : ''}</b>
        {swellDir != null && (
          <span className="cs-fc-arrow sm" style={{ transform: `rotate(${arrowRotation(swellDir)}deg)` }}>↑</span>
        )}
      </span>
      {trains.map((t, i) => (
        <span className="cs-fc-chip cs-fc-chip-train" key={i} title={`${SWELL_TRAIN_LABEL[i] || `#${i + 1}`} raw swell train (deep-water)`}>
          <em style={{ color: SWELL_TRAIN_COLOR[i] }}>{['P1', 'P2', 'P3'][i] || `#${i + 1}`}</em>
          <b style={{ color: '#cdd4dd' }}>{fmtFt(t.ft)}{t.period != null ? ` @ ${fmtPeriod(t.period)}` : ''}</b>
          {t.dir != null && (
            <span className="cs-fc-arrow sm" style={{ transform: `rotate(${arrowRotation(t.dir)}deg)`, color: SWELL_TRAIN_COLOR[i] }}>↑</span>
          )}
        </span>
      ))}
      {cons != null && (
        <span className="cs-fc-chip" title="Set-wave frequency (0-100): how often rideable sets come. Short-period/mixed swell = more sets; long-period groundswell = fewer, bigger sets with lulls.">
          <em>Sets</em>
          <b style={{ color: consistencyColor(cons) }}>{cons}</b>
          <span className="cs-fc-chip-sub">{consistencyLabel(cons)}</span>
        </span>
      )}
      <span className="cs-fc-chip">
        <em>Wind</em>
        <b style={{ color: windColor(windKn) }}>{fmtKn(windKn)}</b>
        {sample.windDir != null && (
          <span className="cs-fc-arrow sm" style={{ transform: `rotate(${arrowRotation(sample.windDir)}deg)`, color: '#c4cad1' }}>↑</span>
        )}
      </span>
      <span className="cs-fc-chip" title={tidePref ? `This spot prefers ${TIDE_MODE_LABEL[tidePref]?.toLowerCase?.() || tidePref} tide` : undefined}>
        <em>Tide</em>
        <b>{tideFt == null ? '—' : `${tideFt.toFixed(1)}ft`}</b>
        {stage01 != null && <span className="cs-fc-chip-sub">{tideStageLabel(stage01)}</span>}
        {sample.tideState && <span className="cs-fc-chip-sub">{tideStateArrow(sample.tideState)}</span>}
        {tidePref && stage01 != null && (
          <span className="cs-fc-chip-sub" style={{ color: tideMatch ? '#43d17f' : '#e0734a' }} title={tideMatch ? 'Tide matches this spot’s preference' : 'Tide is outside this spot’s preferred range'}>
            {tideMatch ? '✓' : '✗'}
          </span>
        )}
      </span>
    </div>
  );
}

export function SpotSettings({ spot }) {
  const th = { ...DEFAULT_THRESHOLDS, ...(spot.thresholds || {}) };
  const [local, setLocal] = useState(th);
  const [face, setFace] = useState(spot.beachFacing ?? '');
  useEffect(() => {
    setLocal({ ...DEFAULT_THRESHOLDS, ...(spot.thresholds || {}) });
    setFace(spot.beachFacing ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot.id]);

  const commit = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSpotThresholds(spot.id, next);
  };
  const numField = (key, label, suffix, step = 1) => (
    <label className="cs-fc-th">
      <span>{label}</span>
      <span className="cs-fc-th-inp">
        <input type="number" step={step} min={0} value={local[key] ?? ''} onChange={(e) => commit({ [key]: e.target.value === '' ? null : Number(e.target.value) })} />
        <em>{suffix}</em>
      </span>
    </label>
  );

  return (
    <div className="cs-fc-thresholds">
      <div className="cs-fc-thgroup">
        <span className="cs-fc-thlabel">Alerts</span>
        <label className="cs-fc-th-toggle">
          <input type="checkbox" checked={local.enabled !== false} onChange={(e) => commit({ enabled: e.target.checked })} />
          On
        </label>
        {numField('minSwellFt', 'Min swell', 'ft', 0.5)}
        {numField('minPeriodS', 'Min period', 's', 1)}
        {numField('maxWindKn', 'Max wind', 'kn', 1)}
        <label className="cs-fc-th">
          <span>Beach faces {Number.isFinite(Number(face)) && face !== '' ? `(${compass16(Number(face))})` : ''}</span>
          <span className="cs-fc-th-inp">
            <input
              type="number"
              step={5}
              min={0}
              max={359}
              value={face}
              onChange={(e) => setFace(e.target.value)}
              onBlur={() => updateSpot(spot.id, { beachFacing: face === '' ? null : ((Number(face) % 360) + 360) % 360 })}
            />
            <em>°</em>
          </span>
        </label>
      </div>
      <StrikeSettings spot={spot} />
      <TideSettings spot={spot} />
      <CalibrationSettings spot={spot} />
    </div>
  );
}

// Per-spot wave-height calibration: map deep-water swell into the breaking/face
// height the spot actually produces. The curve is anchored to a known session so
// it passes through your observed point exactly; the exponents shape how it
// extrapolates to bigger/longer swells (sublinear size so huge swells don't blow
// up linearly). Magnifying breaks (Pascuales, Nazaré…) just need one good anchor.
function CalibrationSettings({ spot }) {
  const fromSpot = () => ({ ...DEFAULT_CALIB, ...(spot.calibration || {}) });
  const [local, setLocal] = useState(fromSpot);
  const [sess, setSess] = useState(() => {
    const a = spot.calibration?.anchor;
    return { face: a?.faceFt ?? '', swell: a?.swellFt ?? '', period: a?.periodS ?? '' };
  });
  useEffect(() => {
    const c = { ...DEFAULT_CALIB, ...(spot.calibration || {}) };
    setLocal(c);
    setSess({ face: c.anchor?.faceFt ?? '', swell: c.anchor?.swellFt ?? '', period: c.anchor?.periodS ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot.id]);

  const commit = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSpotCalibration(spot.id, next);
  };

  const setAnchor = () => {
    const faceFt = Number(sess.face);
    const swellFt = Number(sess.swell);
    const periodS = Number(sess.period);
    if (!(faceFt > 0 && swellFt > 0)) return;
    commit({ enabled: true, anchor: { faceFt, swellFt, periodS: periodS > 0 ? periodS : (local.periodRef || 10) } });
  };
  const canSet = Number(sess.face) > 0 && Number(sess.swell) > 0;
  const hasAnchor = local.anchor && local.anchor.faceFt > 0 && local.anchor.swellFt > 0;

  const exPeriod = local.anchor?.periodS > 0 ? local.anchor.periodS : (Number(sess.period) > 0 ? Number(sess.period) : 13);
  const ex = (hs) => {
    const v = calibrateFt(hs, exPeriod, { ...local, enabled: true });
    return v ? Math.round(v) : '–';
  };

  return (
    <div className="cs-fc-thgroup is-calib">
      <span className="cs-fc-thlabel">🌊 Faces</span>
      <label className="cs-fc-th-toggle">
        <input type="checkbox" checked={local.enabled === true} onChange={(e) => commit({ enabled: e.target.checked })} />
        Calibrate
      </label>
      <label className="cs-fc-th">
        <span>Size sensitivity</span>
        <span className="cs-fc-th-inp">
          <input type="number" step={0.05} min={0.3} max={1.2} value={local.swellExp ?? 0.8} onChange={(e) => commit({ swellExp: e.target.value === '' ? null : Number(e.target.value) })} />
          <em>exp</em>
        </span>
      </label>
      <label className="cs-fc-th">
        <span>Period boost</span>
        <span className="cs-fc-th-inp">
          <input type="number" step={0.1} min={0} max={2} value={local.periodExp ?? 0.5} onChange={(e) => commit({ periodExp: e.target.value === '' ? null : Number(e.target.value) })} />
          <em>exp</em>
        </span>
      </label>
      <span className="cs-fc-th-note">
        face = k · swell<sup>size</sup> · (period/{local.periodRef || 10}s)<sup>boost</sup>, fit to your session. Lower size sensitivity → big swells magnify less.
      </span>
      <div className="cs-fc-calibsess">
        <span className="cs-fc-calibsess-lbl">Anchor session:</span>
        <span className="cs-fc-th-inp">
          <input type="number" step={1} min={0} placeholder="face" value={sess.face} onChange={(e) => setSess({ ...sess, face: e.target.value })} />
          <em>ft from</em>
        </span>
        <span className="cs-fc-th-inp">
          <input type="number" step={0.1} min={0} placeholder="swell" value={sess.swell} onChange={(e) => setSess({ ...sess, swell: e.target.value })} />
          <em>ft @</em>
        </span>
        <span className="cs-fc-th-inp">
          <input type="number" step={1} min={0} placeholder="period" value={sess.period} onChange={(e) => setSess({ ...sess, period: e.target.value })} />
          <em>s</em>
        </span>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!canSet} onClick={setAnchor} title="Fit the curve to this observed session">Set</button>
      </div>
      {hasAnchor && local.enabled && (
        <span className="cs-fc-th-note">
          Anchored: {local.anchor.faceFt}ft from {local.anchor.swellFt}ft @ {local.anchor.periodS}s. Predicts @{exPeriod}s — 6ft→{ex(6)} · 10ft→{ex(10)} · 15ft→{ex(15)}ft.
        </span>
      )}
    </div>
  );
}

// Resolve a spot's effective strike thresholds: size-scaled defaults (derived
// from its calibration anchor so "epic" scales with the break) overlaid with any
// explicit user overrides.
export const strikeFor = (spot) => ({
  ...spotStrikeDefaults(spot.calibration),
  ...(DEFAULT_STRIKES[spot.id] || {}),
  ...(spot.strike || {}),
});

// Per-spot strike-mission thresholds: the bar for "epic" + the wind the spot can
// tolerate (a max speed and a wind-direction arc; outside it, the wind kills it).
function StrikeSettings({ spot }) {
  const init = strikeFor(spot);
  const [local, setLocal] = useState(init);
  useEffect(() => {
    setLocal(strikeFor(spot));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot.id]);

  const commit = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSpotStrike(spot.id, next);
  };
  const num = (key, label, suffix, step = 1) => (
    <label className="cs-fc-th">
      <span>{label}</span>
      <span className="cs-fc-th-inp">
        <input type="number" step={step} min={0} value={local[key] ?? ''} onChange={(e) => commit({ [key]: e.target.value === '' ? null : Number(e.target.value) })} />
        <em>{suffix}</em>
      </span>
    </label>
  );
  const dir = (key, label) => (
    <label className="cs-fc-th">
      <span>{label} {Number.isFinite(local[key]) ? `(${compass16(local[key])})` : ''}</span>
      <span className="cs-fc-th-inp">
        <input type="number" step={5} min={0} max={359} value={local[key] ?? ''} onChange={(e) => commit({ [key]: e.target.value === '' ? null : ((Number(e.target.value) % 360) + 360) % 360 })} />
        <em>°</em>
      </span>
    </label>
  );

  const arc = resolveWindArc(local, Number.isFinite(spot.beachFacing) ? spot.beachFacing : null);
  const autoArc = !Number.isFinite(local.windDirFrom) || !Number.isFinite(local.windDirTo);

  return (
    <div className="cs-fc-thgroup is-strike">
      <span className="cs-fc-thlabel">⚡ Strike</span>
      <label className="cs-fc-th-toggle">
        <input type="checkbox" checked={local.enabled !== false} onChange={(e) => commit({ enabled: e.target.checked })} />
        On
      </label>
      {num('minSwellFt', 'Epic swell ≥', 'ft', 0.5)}
      {num('minPeriodS', 'Period ≥', 's', 1)}
      {num('maxWindKn', 'Wind ≤', 'kn', 1)}
      {dir('windDirFrom', 'Wind OK from')}
      {dir('windDirTo', '…to')}
      {dir('swellDirFrom', 'Swell from')}
      {dir('swellDirTo', '…to')}
      <span className="cs-fc-th-note">
        {autoArc
          ? `Auto offshore band${arc ? ` ${compass16(arc.from)}–${compass16(arc.to)}` : ' (set beach-facing)'}`
          : 'Wind outside this arc disqualifies the window'}
        {Number.isFinite(local.swellDirFrom) && Number.isFinite(local.swellDirTo)
          ? ` · swell only counts from ${compass16(local.swellDirFrom)}–${compass16(local.swellDirTo)}`
          : ''}
      </span>
    </div>
  );
}

// Per-spot tide preference: some breaks only work on a given tide. Picking a
// preset (or a custom stage band) gates that spot's strike + alert windows to
// hours where the tide is in range. 'Any' imposes no constraint (the default).
const TIDE_MODES = ['any', 'low', 'mid', 'high', 'custom'];
function TideSettings({ spot }) {
  const init = { ...DEFAULT_TIDE, ...(spot.tide || {}) };
  const [local, setLocal] = useState(init);
  useEffect(() => {
    setLocal({ ...DEFAULT_TIDE, ...(spot.tide || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot.id]);

  const commit = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSpotTide(spot.id, next);
  };
  const pct = (key, fallback) => (local[key] != null ? Math.round(local[key] * 100) : fallback);

  return (
    <div className="cs-fc-thgroup is-tide">
      <span className="cs-fc-thlabel">🌊 Tide</span>
      <label className="cs-fc-th">
        <span>Works on</span>
        <span className="cs-fc-th-inp">
          <select className="cs-fc-th-sel" value={local.mode || 'any'} onChange={(e) => commit({ mode: e.target.value })}>
            {TIDE_MODES.map((m) => <option key={m} value={m}>{TIDE_MODE_LABEL[m]}</option>)}
          </select>
        </span>
      </label>
      {local.mode === 'custom' && (
        <>
          <label className="cs-fc-th">
            <span>From (0=low)</span>
            <span className="cs-fc-th-inp">
              <input type="number" step={5} min={0} max={100} value={pct('minStage', 30)} onChange={(e) => commit({ minStage: e.target.value === '' ? null : Number(e.target.value) / 100 })} />
              <em>%</em>
            </span>
          </label>
          <label className="cs-fc-th">
            <span>…to (100=high)</span>
            <span className="cs-fc-th-inp">
              <input type="number" step={5} min={0} max={100} value={pct('maxStage', 70)} onChange={(e) => commit({ maxStage: e.target.value === '' ? null : Number(e.target.value) / 100 })} />
              <em>%</em>
            </span>
          </label>
        </>
      )}
      <span className="cs-fc-th-note">
        {local.mode === 'any'
          ? 'No tide constraint — windows fire on any tide'
          : 'Strike & alert windows only fire when the tide is in range'}
      </span>
    </div>
  );
}

const SpotCard = React.forwardRef(function SpotCard({ spot, entry, canEdit, focusNonce }, ref) {
  const cond = entry?.cond || null;
  const err = entry?.err || null;
  const empty = !!entry?.empty;
  const loading = !entry || (!cond && !err && !empty);
  const history = !!cond?.history;
  const [expanded, setExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCams, setShowCams] = useState(false);
  const camCount = Array.isArray(spot.cams) ? spot.cams.length : 0;

  // When a strike suggestion targets this spot, expand it and scroll into view.
  useEffect(() => {
    if (!focusNonce) return;
    setExpanded(true);
    const el = ref && 'current' in ref ? ref.current : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusNonce, ref]);

  const calib = spot.calibration?.enabled ? spot.calibration : null;
  const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
  const alerts = useMemo(() => (cond ? evaluateAlerts(cond, spot.thresholds, calib, spot.tide) : null), [cond, spot.thresholds, calib, spot.tide]);
  const firing = alerts?.alerting;
  // The 🔥 badge is reserved for genuinely epic conditions (a strike window),
  // not the everyday alert bar — otherwise nearly every spot lights up.
  const strikeEval = useMemo(() => {
    if (!cond) return null;
    const st = strikeFor(spot);
    if (st.enabled === false) return null;
    return evaluateStrike(cond, st, beachFacing, calib, spot.tide);
  }, [cond, spot, beachFacing, calib]);
  const epic = !!strikeEval?.best;

  return (
    <div ref={ref} className={`cs-fc-card ${epic ? 'is-firing' : ''} ${expanded ? 'is-expanded' : ''}`}>
      <div className="cs-fc-card-head">
        <button className="cs-fc-title" onClick={() => setExpanded((e) => !e)} title={expanded ? 'Shrink to card width' : 'Expand to full width'}>
          <span className="cs-fc-chev">{expanded ? '▾' : '▸'}</span>
          <span>
            <h3>{spot.name}</h3>
            <span className="cs-fc-region">{spot.region}</span>
          </span>
        </button>
        <div className="cs-fc-head-actions">
          {epic && <span className="cs-fc-badge" title={`Epic strike window — up to ${fmtFt(strikeEval.best.peakFt)}`}>🔥</span>}
          {(camCount > 0 || canEdit) && (
            <button
              className={`cs-btn cs-btn-ghost cs-btn-sm cs-fc-cam-btn ${showCams ? 'on' : ''}`}
              onClick={() => setShowCams((s) => !s)}
              title={camCount ? `Live cams (${camCount})` : 'Add a live cam'}
            >
              📷{camCount > 1 ? <span className="cs-fc-cam-n">{camCount}</span> : null}
            </button>
          )}
          {canEdit && (
            <>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowSettings((s) => !s)} title="Alert & strike thresholds">⚙</button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { if (window.confirm(`Stop tracking ${spot.name}?`)) removeSpot(spot.id); }} title="Remove spot">✕</button>
            </>
          )}
        </div>
      </div>

      {canEdit && showSettings && <SpotSettings spot={spot} />}
      {showCams && <SpotCams spot={spot} canEdit={canEdit} />}

      {loading ? (
        <div className="cs-fc-loading">{history ? 'Loading history…' : 'Loading forecast…'}</div>
      ) : err ? (
        <div className="cs-fc-err">Couldn’t load forecast: {err}</div>
      ) : empty ? (
        <div className="cs-fc-loading">No archived history for this window yet.</div>
      ) : cond && history ? (
        <Meteogram cond={cond} alerts={null} beachFacing={beachFacing} tall={expanded} showLegend={expanded} calib={calib} history />
      ) : cond ? (
        <>
          <NowStrip cond={cond} calib={calib} tide={spot.tide} />
          {firing && (
            <div className="cs-fc-alert on">
              <strong>Next:</strong> {windowLabel(cond, alerts.nextWindow.startSec)}
              {alerts.nextWindow.endSec > alerts.nextWindow.startSec ? ` – ${windowLabel(cond, alerts.nextWindow.endSec)}` : ''} · up to {fmtFt(alerts.nextWindow.peakFt)}
              {alerts.nextWindow.peakPeriod ? ` @ ${fmtPeriod(alerts.nextWindow.peakPeriod)}` : ''}
              {alerts.windows.length > 1 && <em> · {alerts.windows.length} windows</em>}
            </div>
          )}
          <Meteogram cond={cond} alerts={alerts} beachFacing={beachFacing} tall={expanded} showLegend={expanded} calib={calib} />
        </>
      ) : null}
    </div>
  );
});

// Rank tracked spots by their best (sustained) strike window and keep the top 5.
function buildStrikeSuggestions(spots, fmap) {
  const out = [];
  for (const spot of spots || []) {
    const entry = fmap[spot.id];
    if (!entry?.cond) continue;
    const strike = strikeFor(spot);
    if (strike.enabled === false) continue;
    const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
    const calib = spot.calibration?.enabled ? spot.calibration : null;
    const ev = evaluateStrike(entry.cond, strike, beachFacing, calib, spot.tide);
    if (ev.best) out.push({ spot, cond: entry.cond, beachFacing, win: ev.best });
  }
  out.sort((a, b) => b.win.score - a.win.score);
  return out.slice(0, 5);
}

function StrikeCard({ rank, sug, maxScore, onFocus }) {
  const { spot, cond, beachFacing, win } = sug;
  const calibrated = !!(spot.calibration?.enabled && win.peakRawFt != null);
  const ptr = win.peakHour ? swellTrains(win.peakHour) : null;
  const ptrains = ptr ? [ptr.primary, ptr.secondary, ptr.tertiary].filter((t) => t && t.ft != null) : [];
  const wq = win.peakWindDir != null ? windQuality(win.peakWindDir, beachFacing, win.peakWindKn) : null;
  const pct = Math.max(10, Math.round((win.score / (maxScore || win.score)) * 100));
  const eta = daysOut(win.startSec);
  return (
    <button className="cs-strike-card" onClick={() => onFocus(spot.id)} title={`Open ${spot.name}`}>
      <div className="cs-strike-top">
        <span className="cs-strike-rank">{rank}</span>
        <span className="cs-strike-name">
          <h3>{spot.name}</h3>
          <span className="cs-strike-region">{spot.region}</span>
        </span>
        <span className="cs-strike-eta">{eta === 0 ? 'now' : `+${eta}d`}</span>
      </div>
      <div className="cs-strike-peak" style={{ color: swellColor(win.peakFt) }}>
        {fmtFt(win.peakFt)}{calibrated && <em> face</em>}
        {win.peakDir != null && <span className="cs-fc-arrow sm" style={{ transform: `rotate(${arrowRotation(win.peakDir)}deg)` }}>↑</span>}
      </div>
      <div className="cs-strike-swell">
        {calibrated
          ? `${fmtFt(win.peakRawFt)} swell${win.peakPeriod ? ` @ ${fmtPeriod(win.peakPeriod)}` : ''}`
          : (win.peakPeriod ? `${fmtPeriod(win.peakPeriod)} period` : '')}
      </div>
      {ptrains.length > 0 && (
        <div className="cs-strike-trains">
          {ptrains.map((t, i) => (
            <span key={i}>
              <em style={{ color: SWELL_TRAIN_COLOR[i] }}>{['P1', 'P2', 'P3'][i] || `#${i + 1}`}</em>
              {fmtFt(t.ft)}{t.period != null ? `·${fmtPeriod(t.period)}` : ''}
            </span>
          ))}
        </div>
      )}
      <div className="cs-strike-when">{dayLabel(cond, win.startSec)} · {durationLabel(win.durationH)} epic</div>
      <div className="cs-strike-wind">
        <i style={{ background: (wq && WIND_QUALITY_COLOR[wq]) || '#9aa0a6' }} />
        {fmtKn(win.peakWindKn)}{win.peakWindDir != null ? ` ${dirLabel(win.peakWindDir, false)}` : ''}{wq ? ` · ${WIND_QUALITY_LABEL[wq]}` : ''}
      </div>
      {win.peakCons != null && (
        <div className="cs-strike-wind">
          <i style={{ background: consistencyColor(win.peakCons) }} />
          {win.peakCons} sets · {consistencyLabel(win.peakCons)}
        </div>
      )}
      <div className="cs-strike-meter"><span style={{ width: `${pct}%` }} /></div>
    </button>
  );
}

// Size-aware color ramp for strike bars (faces run far bigger than swellColor's
// 8ft ceiling, so give the Gantt its own small→XXL scale).
function strikeColor(ft) {
  if (ft == null) return '#3aa0ff';
  if (ft < 5) return '#34b3d6';
  if (ft < 8) return '#3aa0ff';
  if (ft < 12) return '#5b8def';
  if (ft < 18) return '#9b6cf2';
  if (ft < 28) return '#e0517a';
  return '#e0734a';
}

// Home base: Los Angeles. The strike calendar is grouped by geographical region
// and regions are ordered by how far they are from home, so the closest waves
// (California, Mexico) sit at the top and the board branches consecutively out.
const HOME = { lat: 34.0522, lng: -118.2437 };
function distMi(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return Infinity;
  const R = 3958.8;
  const dLat = ((lat - HOME.lat) * Math.PI) / 180;
  const dLng = ((lng - HOME.lng) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((HOME.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
// Coarse geographical zone for a spot, derived from its region string (with a
// distance fallback so unclassified spots still slot in roughly by proximity).
function spotZone(spot) {
  const r = (spot.region || '').toLowerCase();
  if (r.includes('hawaii')) return 'Hawaii';
  if (r.includes('california') || r.includes('usa') || r.includes('oregon')) return 'California';
  if (r.includes('mexico') || r.includes('baja')) return 'Mexico';
  if (/nicaragua|costa rica|panama|el salvador|guatemala|honduras/.test(r)) return 'Central America';
  if (/french polynesia|tahiti|fiji|samoa|tonga|new zealand|australia/.test(r)) return 'South Pacific';
  if (/portugal|france|spain|morocco|ireland|england|uk|canary/.test(r)) return 'Europe / NE Atlantic';
  if (r.includes('philippines')) return 'Philippines';
  if (r.includes('indonesia') || r.includes('mentawai') || r.includes('bali')) return 'Indonesia';
  if (/peru|chile|ecuador|brazil/.test(r)) return 'South America';
  if (/south africa|namibia/.test(r)) return 'Africa';
  return spot.region || 'Other';
}

// Collect every strike window (not just the best) for every enabled spot, for
// the Gantt timeline. Rows are grouped by region (regions ordered by distance
// from home) and, within a region, nearest-first; a spot can contribute
// multiple bars.
function buildStrikeTimeline(spots, fmap) {
  const rows = [];
  for (const spot of spots || []) {
    const entry = fmap[spot.id];
    if (!entry?.cond) continue;
    const strike = strikeFor(spot);
    if (strike.enabled === false) continue;
    const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
    const calib = spot.calibration?.enabled ? spot.calibration : null;
    const ev = evaluateStrike(entry.cond, strike, beachFacing, calib, spot.tide);
    if (!ev.windows.length) continue;
    const wins = [...ev.windows].sort((a, b) => a.startSec - b.startSec).slice(0, 6);
    rows.push({ spot, cond: entry.cond, beachFacing, calib: !!calib, wins, best: ev.best, zone: spotZone(spot), dist: distMi(spot.lat, spot.lng) });
  }
  // Order regions by their closest spot to home, then spots nearest-first.
  const zoneMin = {};
  rows.forEach((r) => { zoneMin[r.zone] = Math.min(zoneMin[r.zone] ?? Infinity, r.dist); });
  rows.sort((a, b) => (zoneMin[a.zone] - zoneMin[b.zone]) || (a.dist - b.dist) || (b.best.score - a.best.score));
  return rows;
}

const GDAY = 86400;
const GSPAN = 16; // days — matches the forecast model's 16-day horizon

// Gantt-style strike calendar: a 16-day timeline with one row per spot and a
// horizontal bar for each epic window, so overlapping strike opportunities read
// at a glance. Bars are sized by window length, colored by peak face height.
function StrikeGantt({ spots, fmap, onFocus }) {
  const rows = useMemo(() => buildStrikeTimeline(spots, fmap), [spots, fmap]);
  const anyLoading = (spots || []).some((s) => !fmap[s.id] || fmap[s.id]?.loading);
  const [tip, setTip] = useState(null);
  // seg is the stats source: a per-day slice of the window (same peak* field
  // names) so hovering a day segment reads THAT day's peak, not the window's.
  const showTip = (e, r, w, seg) => {
    const s = seg || w;
    const wq = s.peakWindDir != null ? windQuality(s.peakWindDir, r.beachFacing, s.peakWindKn) : null;
    const tr = s.peakHour ? swellTrains(s.peakHour) : null;
    setTip({
      x: e.clientX, y: e.clientY,
      name: r.spot.name,
      when: `${dayLabel(r.cond, s.startSec)} · day peak · ${durationLabel(w.durationH)} epic window`,
      face: s.peakFt, dir: s.peakDir, period: s.peakPeriod,
      calib: r.calib, raw: s.peakRawFt, cons: s.peakCons,
      trains: tr ? [tr.primary, tr.secondary, tr.tertiary].filter((t) => t && t.ft != null) : null,
      windKn: s.peakWindKn, windDir: s.peakWindDir, wq,
    });
  };

  const { days, frac, nowFrac, confFrac } = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const start = Math.round(d.getTime() / 1000);
    const end = start + GSPAN * GDAY;
    const fr = (t) => Math.max(0, Math.min(1, (t - start) / (end - start)));
    const list = Array.from({ length: GSPAN }, (_, i) => {
      const ds = new Date((start + i * GDAY) * 1000);
      const dom = ds.getDate();
      return {
        i,
        dom,
        mon: ds.toLocaleDateString(undefined, { month: 'short' }),
        showMon: i === 0 || dom === 1,
        weekend: ds.getDay() === 0 || ds.getDay() === 6,
      };
    });
    return { days: list, frac: fr, nowFrac: fr(Date.now() / 1000), confFrac: fr(start + CONFIDENCE_DAYS * GDAY) };
  }, []);

  if (!rows.length) {
    return (
      <div className="cs-strike-empty cs-help">
        {anyLoading ? 'Scanning forecasts for epic windows…' : 'No epic windows on the 16-day board. Tune each spot’s ⚡ strike thresholds (⚙ on a card) to widen the net.'}
      </div>
    );
  }

  const colW = 100 / GSPAN;
  return (
    <div className="cs-gantt">
      <div className="cs-gantt-head">
        <div className="cs-gantt-corner" />
        <div className="cs-gantt-axis">
          {confFrac < 1 && <span className="cs-gantt-ext" style={{ left: `${confFrac * 100}%`, right: 0 }}><span className="cs-gantt-ext-lbl">extended · lower confidence</span></span>}
          {days.map((d) => (
            <span key={d.i} className={`cs-gantt-daycell ${d.weekend ? 'is-weekend' : ''}`} style={{ left: `${d.i * colW}%`, width: `${colW}%` }}>
              {d.showMon ? <em>{d.mon} </em> : null}{d.dom}
            </span>
          ))}
          {nowFrac > 0 && nowFrac < 1 && <span className="cs-gantt-nowlbl" style={{ left: `${nowFrac * 100}%` }}>now</span>}
        </div>
      </div>
      <div className="cs-gantt-body">
        {rows.map((r, idx) => (
          <React.Fragment key={r.spot.id}>
          {(idx === 0 || rows[idx - 1].zone !== r.zone) && (
            <div className="cs-gantt-group"><span>{r.zone}</span></div>
          )}
          <div className="cs-gantt-row">
            <button className="cs-gantt-lbl" onClick={() => onFocus(r.spot.id)} title={`Open ${r.spot.name}`}>
              <span className="cs-gantt-lbl-name">{r.spot.name}</span>
              <span className="cs-gantt-lbl-region">{r.spot.region}</span>
            </button>
            <div className="cs-gantt-track">
              {days.map((d) => (
                <span key={d.i} className={`cs-gantt-grid ${d.weekend ? 'is-weekend' : ''}`} style={{ left: `${d.i * colW}%`, width: `${colW}%` }} />
              ))}
              {confFrac < 1 && <span className="cs-gantt-extband" style={{ left: `${confFrac * 100}%`, right: 0 }} />}
              {nowFrac > 0 && nowFrac < 1 && <span className="cs-gantt-now" style={{ left: `${nowFrac * 100}%` }} />}
              {r.wins.map((w, i) => {
                // One segment per day of the window (clipped to local-midnight
                // columns) so a multi-day swell shows each day's own peak size
                // instead of one flat bar at the window-wide peak.
                const segs = w.days?.length ? w.days : [{ ...w, daySec: null }];
                const minSeg = Math.max(1.4 / segs.length, 0.45);
                const geom = segs.map((d) => {
                  const segStart = d.daySec == null ? w.startSec : Math.max(w.startSec, d.daySec);
                  const segEnd = d.daySec == null ? w.endSec : Math.min(w.endSec, d.daySec + GDAY);
                  const left = frac(segStart) * 100;
                  const width = Math.max(minSeg, (frac(segEnd) - frac(segStart)) * 100);
                  return { left, width, wide: width >= 3 };
                });
                // Labels: wide segments label in-bar. If NO segment is wide (a
                // small blip of a window), the peak day spills its label past
                // the bar so short windows still read a height. Tiny
                // midnight-clipped edge slices of an otherwise-labeled window
                // stay quiet — their neighbor's label already tells the story.
                const anyWide = geom.some((g) => g.wide);
                const peakJ = segs.reduce((bi, d, j) => ((d.peakFt ?? 0) > (segs[bi].peakFt ?? 0) ? j : bi), 0);
                return segs.map((d, j) => {
                  const { left, width, wide } = geom[j];
                  const wq = d.peakWindDir != null ? windQuality(d.peakWindDir, r.beachFacing, d.peakWindKn) : null;
                  const first = j === 0;
                  const last = j === segs.length - 1;
                  const spill = !anyWide && j === peakJ;
                  return (
                    <button
                      key={`${i}-${j}`}
                      className={`cs-gantt-bar${spill ? ' is-tiny' : ''}`}
                      style={{
                        left: `${left}%`, width: `${width}%`,
                        background: strikeColor(d.peakFt),
                        borderRadius: `${first ? '4px' : '0'} ${last ? '4px 4px' : '0 0'} ${first ? '4px' : '0'}`,
                      }}
                      onClick={() => onFocus(r.spot.id)}
                      onMouseEnter={(e) => showTip(e, r, w, d)}
                      onMouseMove={(e) => showTip(e, r, w, d)}
                      onMouseLeave={() => setTip(null)}
                    >
                      {(wide || spill) && <span className="cs-gantt-bar-lbl">{fmtFt(d.peakFt)}</span>}
                      {wq && width >= 4.5 && <i className="cs-gantt-bar-wind" style={{ background: WIND_QUALITY_COLOR[wq] }} />}
                    </button>
                  );
                });
              })}
            </div>
          </div>
          </React.Fragment>
        ))}
      </div>
      <div className="cs-gantt-legend cs-help">
        Each bar is an epic window (length = duration); each day segment is colored + labeled by THAT day&apos;s peak face. Dot = the day&apos;s wind quality. Hover for detail · click to open the spot.
      </div>
      {tip && (
        <div
          className="cs-gantt-tip"
          style={{ left: `${Math.max(96, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) - 96, tip.x))}px`, top: `${tip.y}px` }}
        >
          <div className="cs-gantt-tip-time">{tip.name} · {tip.when}</div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">{tip.calib ? 'Face' : 'Swell'}</span>
            <span className="cs-meteo-tip-v" style={{ color: swellColor(tip.face) }}>
              {fmtFt(tip.face)}{tip.period != null ? ` @ ${fmtPeriod(tip.period)}` : ''}{tip.dir != null ? ` ${dirLabel(tip.dir, false)}` : ''}
            </span>
          </div>
          {tip.calib && tip.raw != null && (
            <div className="cs-meteo-tip-row">
              <span className="cs-meteo-tip-k">Swell</span>
              <span className="cs-meteo-tip-v" style={{ color: '#9aa6b5' }}>
                {fmtFt(tip.raw)}{tip.period != null ? ` @ ${fmtPeriod(tip.period)}` : ''}
              </span>
            </div>
          )}
          <SwellTipRows trains={tip.trains} />
          {tip.cons != null && (
            <div className="cs-meteo-tip-row">
              <span className="cs-meteo-tip-k">Sets</span>
              <span className="cs-meteo-tip-v">
                <i style={{ background: consistencyColor(tip.cons) }} />
                {tip.cons} · {consistencyLabel(tip.cons)}
              </span>
            </div>
          )}
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Wind</span>
            <span className="cs-meteo-tip-v">
              <i style={{ background: (tip.wq && WIND_QUALITY_COLOR[tip.wq]) || '#9aa0a6' }} />
              {fmtKn(tip.windKn)}{tip.windDir != null ? ` ${dirLabel(tip.windDir, false)}` : ''}{tip.wq ? ` · ${WIND_QUALITY_LABEL[tip.wq]}` : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StrikeMissions({ spots, fmap, onFocus }) {
  const sugs = useMemo(() => buildStrikeSuggestions(spots, fmap), [spots, fmap]);
  const anyLoading = (spots || []).some((s) => !fmap[s.id] || fmap[s.id]?.loading);
  if (!sugs.length) {
    return (
      <div className="cs-strike-empty cs-help">
        {anyLoading
          ? 'Scanning forecasts for epic windows…'
          : 'No epic windows on the board in the next 16 days. Tune each spot’s ⚡ strike thresholds (⚙ on a card) to widen the net.'}
      </div>
    );
  }
  const maxScore = sugs[0].win.score;
  return (
    <div className="cs-strike-grid">
      {sugs.map((s, i) => (
        <StrikeCard key={s.spot.id} rank={i + 1} sug={s} maxScore={maxScore} onFocus={onFocus} />
      ))}
    </div>
  );
}

function AddSpotForm({ onClose, count }) {
  const [form, setForm] = useState({ name: '', region: '', lat: '', lng: '', beachFacing: '' });
  const [busy, setBusy] = useState(false);
  const valid = form.name.trim() && Number.isFinite(Number(form.lat)) && Number.isFinite(Number(form.lng));
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await addSpot(form, count);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cs-fc-addform">
      <div className="cs-fc-addrow">
        <input className="cs-input cs-input-sm" placeholder="Spot name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Region (optional)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Beach faces° (optional)" value={form.beachFacing} onChange={(e) => setForm({ ...form, beachFacing: e.target.value })} />
        <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!valid || busy} onClick={submit}>Add</button>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Cancel</button>
      </div>
      <div className="cs-help" style={{ margin: '6px 0 0' }}>
        Tip: grab coordinates from Google Maps (right-click → the lat, lng). “Beach faces°” is the compass bearing the beach looks out to sea — it powers offshore/onshore wind coloring.
      </div>
    </div>
  );
}

export default function Forecast() {
  const { user } = useAuth();
  const spots = useForecastSpots();
  const [refreshKey, setRefreshKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [focus, setFocus] = useState({ id: null, nonce: 0 });
  const cardRefs = useRef({});

  const liveMap = useAllForecasts(spots, refreshKey);

  // Live / History window (shared selector). In History mode the per-spot cards
  // read sliced archive history; the forward-looking strike sections are hidden.
  const { manifest } = useBasinArchive('world');
  const extent = manifest ? { minTime: manifest.minTime, maxTime: manifest.maxTime } : null;
  const [win, setWin] = useState({ mode: 'live' });
  const isHistory = win.mode === 'history';
  const histMap = useAllSpotArchives(spots, win);
  const fmap = isHistory ? histMap : liveMap;

  // Strike windows for the compact world map (same timeline the Gantt uses).
  const strikeRows = useMemo(
    () => (spots && !isHistory ? buildStrikeTimeline(spots, liveMap) : []),
    [spots, liveMap, isHistory]
  );

  useEffect(() => {
    // Only signed-in admins seed the shared spot list (writes are gated). Once
    // seeded, top up any newly shipped default spots that are missing.
    if (!user || !spots) return;
    if (spots.length === 0) seedDefaultSpots().catch(() => {});
    else {
      ensureDefaultSpots(spots).catch(() => {});
      ensureSpotCalibrations(spots).catch(() => {});
      ensureSpotCams(spots).catch(() => {});
    }
  }, [user, spots]);

  const refFor = (id) => {
    if (!cardRefs.current[id]) cardRefs.current[id] = React.createRef();
    return cardRefs.current[id];
  };
  const onFocus = (id) => setFocus((f) => ({ id, nonce: f.nonce + 1 }));

  const loading = spots === null;

  return (
    <div className="cs-page cs-fc-page">
      <div className="cs-fc-header">
        <div>
          <h1>Surf Forecast</h1>
          <p className="cs-fc-sub">Live 16-day swell, wind &amp; tide with strike-mission scouting and custom alerts. Data: Open-Meteo (free, no API key).</p>
        </div>
        {!isHistory && !loading && spots.length > 0 && (
          <StrikeMiniMap spots={spots} rows={strikeRows} onFocus={onFocus} />
        )}
        <div className="cs-fc-controls">
          <SwellWindow extent={extent} value={win} onChange={setWin} />
          {!isHistory && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setRefreshKey((k) => k + 1)} title="Refresh all forecasts">↻ Refresh</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="cs-fc-loading">Loading spots…</div>
      ) : spots.length === 0 ? (
        <div className="cs-empty">No spots tracked yet.</div>
      ) : (
        <>
          {!isHistory && (
            <Section id="strike-cal" title="Strike calendar">
              <StrikeGantt spots={spots} fmap={liveMap} onFocus={onFocus} />
            </Section>
          )}

          <Section
            id="spots"
            title={isHistory ? 'Tracked spots · history' : 'Tracked spots'}
            right={
              user && (
                <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => setAdding((a) => !a)}>
                  {adding ? 'Close' : '+ Add spot'}
                </button>
              )
            }
          >
            {user && adding && <AddSpotForm onClose={() => setAdding(false)} count={spots?.length || 0} />}
            <div className="cs-fc-grid">
              {spots.map((s) => (
                <SpotCard
                  key={s.id}
                  ref={refFor(s.id)}
                  spot={s}
                  entry={fmap[s.id]}
                  canEdit={!!user}
                  focusNonce={focus.id === s.id ? focus.nonce : 0}
                />
              ))}
            </div>
          </Section>

          {!isHistory && (
            <Section id="strike" title="Strike missions">
              <StrikeMissions spots={spots} fmap={liveMap} onFocus={onFocus} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}
